
















































































































































import { Component, Watch, Mixins } from "vue-property-decorator";
import { Getter } from "vuex-class";
import MediaSelect from "@/components/MediaSelect.vue";
import TimeSlotPicker from "@/components/TimeSlotPicker.vue";
import DatePicker, {
  DATE_FORMAT,
  DatePickerData,
  DateString,
} from "@/components/DatePicker.vue";
import SelectOption from "@/interfaces/SelectOption";
import VMultiSwitcher from "@/components/VMultiSwitcher.vue";
import VSwitchButton from "@/components/VSwitchButton.vue";
import VCheckButton from "@/components/VCheckButton.vue";
import CustomChart, { CustomChartItem } from "@/components/CustomChart.vue";
import ComparisonGroupSelect from "@/components/ComparisonGroupSelect.vue";
import InvestorsMixins from "@/mixins/http/Investor";
import ClickableEntryList from "@/mixins/ClickableEntryListener";
import { ComparisonGroup, Target, User } from "@/models";
import HeatmapTable, {
  HeatmapTableColumn,
  HeatmapTableRow,
} from "@/components/HeatmapTable.vue";
import EntryItem from "@/components/EntryItem.vue";
import NumberFormatter from "@/services/NumberFormatter";
import MediaValue from "../../interfaces/MediaValue";
import DaysComparatorChart from "@/components/Investor/DaysComparatorChart.vue";
import { FEATURE_AUDITEL } from "@/constants";
import GrpTargetSelect from "@/components/GrpTargetSelect.vue";
import MultiLevelSelect from "@/components/MultiLevelSelect.vue";
import MultiLevelObject from "@/interfaces/MultiLevelObject";
import IntervalCount from "@/interfaces/IntervalCount";
import Disclaimer from "@/components/GrpTargets/Disclaimer.vue";
import QueryParser from "@/services/QueryParser";
import QueryHelper from "@/services/QueryHelper";
import PageGuide, { GuidePaths } from "@/components/PageGuide.vue";

const requestTag = "comparison";

interface DateRange {
  from: DateString;
  to: DateString;
}

interface InvestorComparatorFilter {
  group: ComparisonGroup | null;
  unitOfMeasurement: string;
  media: MediaValue;
  date: DateRange;
  granularity: string;
  grpTarget: string | null;
}

@Component({
  components: {
    MediaSelect,
    TimeSlotPicker,
    DatePicker,
    VCheckButton,
    VMultiSwitcher,
    VSwitchButton,
    DaysComparatorChart,
    ComparisonGroupSelect,
    CustomChart,
    HeatmapTable,
    EntryItem,
    GrpTargetSelect,
    MultiLevelSelect,
    Disclaimer,
    PageGuide,
  },
})
export default class InvestorDaysComparator extends Mixins(
  InvestorsMixins,
  ClickableEntryList
) {
  @Getter("lastDate") readonly lastDate!: string;
  @Getter("userMedia") readonly userMedia!: MediaValue;
  @Getter("user") readonly user!: User;
  @Getter readonly parseDateValue!: (
    date: string,
    format: string,
    defaultOffset?: number,
    // eslint-disable-next-line
    defaultUnit?: any
  ) => string;
  @Getter readonly parseAndCleanMediaValue!: (
    radio: string | boolean | undefined,
    tv: string | boolean | undefined
  ) => MediaValue;

  @Watch("filter", { deep: true })
  onFilterChange() {
    if (this.filter.media.radio) {
      if (
        this.filter.unitOfMeasurement == "grp" ||
        this.filter.unitOfMeasurement == "grp_over_spots" ||
        this.filter.unitOfMeasurement == "audience"
      ) {
        this.filter.unitOfMeasurement = "spots";
      }
    }
    const isEqual = QueryHelper.isSameQuery(
      this.$route.query,
      this.queryFilter
    );
    if (!isEqual) {
      this.$router.replace({ query: { ...this.queryFilter } });
    }
    this.load();
  }

  filter: InvestorComparatorFilter = {
    group: null,
    date: {
      from: this.$moment(this.lastDate)
        .subtract(7, "days")
        .format(DATE_FORMAT) as DateString,
      to: this.$moment(this.lastDate).format(DATE_FORMAT) as DateString,
    },
    grpTarget: null,
    granularity: "wday",
    media: {
      radio: true,
      tv: true,
    },
    unitOfMeasurement: "spots",
  };

  target: Target | null = null;

  valueInSeconds = false;
  unitOfMeasurementsAuditel: MultiLevelObject[] = [
    {
      id: "spots",
      text: "Spot",
    },
    {
      id: "seconds",
      text: "Secondi",
    },
    {
      id: "grp",
      text: "GRP",
    },
    {
      id: "grp_over_spots",
      text: "GRP / Spot",
    },
    {
      id: "audience",
      text: "Contatti",
    },
  ];
  unitOfMeasurementsNoAuditel: MultiLevelObject[] = [
    {
      id: "spots",
      text: "Spot",
    },
    {
      id: "seconds",
      text: "Secondi",
    },
  ];

  heatmap = false;

  chart = {
    settings: {
      stacked: false,
      percentage: false,
      type: "bar",
    },
  };
  items = [];
  hiddenItems: string[] = [];
  loading = false;

  private granularityItems: Array<SelectOption> = [
    { id: "wday", text: "Giorni" },
    { id: "hour", text: "Fasce orarie" },
    { id: "weekperiod", text: "Week Parts" },
  ];

  private chartTypeItems: Array<SelectOption> = [
    { id: "bar", text: "Barre" },
    { id: "line", text: "Linea" },
    { id: "pie", text: "Torta", disabled: true },
  ];

  onValueInSecondChanged(value: boolean) {
    this.valueInSeconds = value;
  }

  guidePath = GuidePaths.INVESTOR_DAYS_COMPARATOR;

  get queryFilter() {
    return {
      from: this.filter.date.from as string,
      to: this.filter.date.to as string,
      radio: this.filter.media.radio ? "1" : "0",
      tv: this.filter.media.tv ? "1" : "0",
      unit: this.filter.unitOfMeasurement,
      granularity: this.filter.granularity,
    };
  }

  get unitOfMeasurements() {
    if (this.hasAuditelCapability && this.filter.media.radio == false) {
      return this.unitOfMeasurementsAuditel;
    }
    return this.unitOfMeasurementsNoAuditel;
  }

  get hasAuditelCapability() {
    return this.user.hasFeature(FEATURE_AUDITEL);
  }

  get disabled(): boolean {
    return this.filter.group == undefined || this.loading;
  }

  get chartLabels(): string[] {
    let start = this.$moment(this.filter.date.from);
    let end = this.$moment(this.filter.date.to).add(1, "days");

    const labels = new Set();

    switch (this.filter.granularity) {
      case "wday":
        start = this.$moment().startOf("week");
        end = this.$moment().endOf("week");

        for (let date = start; date.isBefore(end); date.add(1, "days")) {
          const label = date.format("dddd");
          labels.add(label.charAt(0).toUpperCase() + label.slice(1));
        }

        break;

      case "hour":
        start = this.$moment().hour(6).minutes(0);
        end = this.$moment().add(1, "days").hours(5).minutes(59);
        for (let time = start; time.isBefore(end); time.add(1, "hours")) {
          const label = time.format("HH:mm");
          labels.add(label);
        }

        break;

      case "weekperiod":
        labels.add("Week-days");
        labels.add("Week-ends");

        break;
    }

    return [...labels] as string[];
  }

  get tableColumns(): HeatmapTableColumn[] {
    const columns: HeatmapTableColumn[] = [
      {
        name: "entry",
        label: "Azienda",
        notSortable: true,
        heading: true,
      },
    ];

    this.chartLabels.forEach((label, index) => {
      columns.push({
        name: `interval${index}`,
        label: label,
        format: this.formatCellValue,
      });
    });

    return columns;
  }

  get tableRows(): HeatmapTableRow[] {
    const rows: HeatmapTableRow[] = [];

    // eslint-disable-next-line
    this.items.forEach((item: any) => {
      const row: HeatmapTableRow = {
        entry: item,
      };

      item.timeIntervals.forEach((interval: IntervalCount, index: number) => {
        row[`interval${index}`] = this.getIntervalDetections(interval);
      });

      if (this.hiddenItems.indexOf(item.id) == -1) {
        rows.push(row);
      }
    });

    return rows;
  }

  buildFilterFromQuery(): InvestorComparatorFilter {
    const query = this.$route.query;
    const { radio, tv } = query;

    const from = this.parseDateValue(
      query.from as string,
      DATE_FORMAT,
      6,
      "days"
    );
    const to = this.parseDateValue(query.to as string, DATE_FORMAT);

    const media = this.parseAndCleanMediaValue(
      QueryParser.queryToBoolean(radio),
      QueryParser.queryToBoolean(tv)
    );
    const granularity = query.granularity
      ? (query.granularity as string)
      : this.filter.granularity;

    return {
      group: this.filter.group,
      unitOfMeasurement: (query.unit as string) ?? "spots",
      media,
      granularity,
      grpTarget: this.filter.grpTarget,
      date: {
        from,
        to,
      },
    };
  }

  created() {
    this.filter = this.buildFilterFromQuery();
    this.load();
  }

  async load() {
    if (this.disabled || this.filter.group == null) {
      return;
    }

    const group = this.filter.group as unknown as ComparisonGroup;

    this.loading = true;
    this.items = [];
    this.items = await this.getComparisons(
      group.id,
      this.getPostData(),
      requestTag
    );
    this.items.forEach((item: any) => {
      item.timeIntervals = item.timeIntervals.map((timeInterval: any) => {
        return Object.assign({}, timeInterval, {
          grpCount: Number.parseFloat(
            Number.parseFloat(timeInterval.grpCount).toFixed(2)
          ),
        });
      });
    });

    if (this.filter.granularity == "hour") {
      // eslint-disable-next-line
      this.items.map((item: any) => {
        const before6 = item.timeIntervals.slice(0, 6);
        const after6 = item.timeIntervals.slice(6, item.timeIntervals.length);
        item.timeIntervals = [...after6, ...before6];
        return item;
      });
    }

    this.loading = false;
  }

  formatCellValue(value: string | number | undefined) {
    if (value == undefined || value == 0 || value == "0") {
      return "-";
    }
    if (
      this.filter.unitOfMeasurement == "grp" ||
      this.filter.unitOfMeasurement == "grp_over_spots"
    ) {
      return NumberFormatter.format(value as number, 2);
    }
    return NumberFormatter.format(value as number);
  }

  getIntervalDetections(interval: IntervalCount) {
    let value = 0;

    if (this.filter.unitOfMeasurement === "seconds") {
      if (this.filter.media.tv) {
        value += interval.tvSecondsCount as number;
      }
      if (this.filter.media.radio) {
        value += interval.radioSecondsCount as number;
      }
    }
    if (this.filter.unitOfMeasurement === "spots") {
      if (this.filter.media.tv) {
        value += interval.tvDetectionsCount as number;
      }
      if (this.filter.media.radio) {
        value += interval.radioDetectionsCount as number;
      }
    }
    if (this.filter.unitOfMeasurement === "grp") {
      if (this.filter.media.tv) {
        value += interval.grpCount ? (interval.grpCount as number) : 0;
      }
    }
    if (this.filter.unitOfMeasurement === "grp_over_spots") {
      if (this.filter.media.tv) {
        value += interval.grpCount
          ? interval.tvDetectionsCount
            ? (interval.grpCount as number) / interval.tvDetectionsCount
            : 0
          : 0;
      }
    }
    if (this.filter.unitOfMeasurement === "audience") {
      if (this.filter.media.tv) {
        value += interval.audienceCount
          ? (interval.audienceCount as number)
          : 0;
      }
    }

    return value;
  }

  getPostData() {
    const result = {
      filter: {
        date_range: [this.filter.date.from, this.filter.date.to],
      },
      granularity: this.filter.granularity,
    };
    if (this.hasAuditelCapability && this.filter.grpTarget) {
      return Object.assign({}, result, { grpTarget: this.filter.grpTarget });
    }
    return result;
  }

  onDateSelected(data: DatePickerData) {
    if (data.range) {
      this.filter.date = data.range;
    }
  }

  onChartItemHidden(index: number) {
    const item = this.items[index];

    if (item) {
      this.hiddenItems.push((item as { id: string }).id);
    }
  }

  onChartItemVisible(index: number) {
    const item = this.items[index];

    if (item) {
      const hiddenIndex = this.hiddenItems.indexOf((item as { id: string }).id);

      if (hiddenIndex != -1) {
        this.hiddenItems.splice(hiddenIndex, 1);
      }
    }
  }

  onGrpTargetChanged(target: Target) {
    this.target = target;
    this.filter.grpTarget = this.target?.id;
  }

  setDefaultGranularity() {
    const start = this.$moment(this.filter.date.from);
    const end = this.$moment(this.filter.date.to);
    const days = end.diff(start, "days") + 1;

    if (days > 31) {
      return "month";
    } else if (days > 7) {
      return "sweek";
    } else {
      return "day";
    }
  }
}
